import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Users, UserCheck, CalendarDays, ArrowRight, UserPlus, Euro, TrendingUp,
    AlertTriangle, CreditCard, Percent,
} from "lucide-react";
import {
    BarChart, Bar, PieChart, Pie, Cell, Tooltip, ResponsiveContainer, XAxis, YAxis,
    CartesianGrid, Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PALETTE = ["#E8873C", "#0A0A0A", "#D9772C", "#8A8A8A", "#C4632A"];

function KpiCard({ icon: Icon, label, value, tone = "default", testId, suffix = "" }) {
    const toneCls = tone === "orange" ? "bg-brand-orange text-white" : "bg-white border border-brand-line";
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`rounded-2xl p-6 shadow-premium ${toneCls}`}
            data-testid={testId}
        >
            <div className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${tone === "orange" ? "bg-white/20" : "bg-brand-orange/10 text-brand-orange"}`}>
                <Icon className="h-5 w-5" />
            </div>
            <div className={`mt-4 font-display text-4xl leading-none ${tone === "orange" ? "" : "text-brand-ink"}`}>
                {value}
                {suffix && <span className={`ml-1 text-xl ${tone === "orange" ? "text-white/70" : "text-brand-smoke"}`}>{suffix}</span>}
            </div>
            <div className={`mt-3 text-xs uppercase tracking-wider ${tone === "orange" ? "text-white/80" : "text-brand-smoke"}`}>
                {label}
            </div>
        </motion.div>
    );
}

function formatDateFR(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/stats/advanced")
            .then((r) => setStats(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    const kpis = stats?.kpis || {};

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="admin-dashboard">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="font-display text-5xl font-normal tracking-tightest">Dashboard</h1>
                    <p className="mt-2 text-brand-smoke">Vue d'ensemble de l'activité Cursus Physio.</p>
                </div>
                <div className="flex gap-3">
                    <Link to="/admin/sessions" data-testid="admin-dashboard-new-session" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-[#d9772c]">
                        <CalendarDays className="h-4 w-4" /> Nouvelle session
                    </Link>
                    <Link to="/admin/utilisateurs" data-testid="admin-dashboard-goto-users" className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-5 py-3 text-sm font-semibold text-brand-ink hover:border-brand-orange">
                        <UserPlus className="h-4 w-4" /> Nouveau formateur
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className="mt-10 text-brand-smoke">Chargement…</div>
            ) : (
                <>
                    <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        <KpiCard icon={Euro} label="Chiffre d'affaires total" value={kpis.ca_total ?? 0} suffix="€" tone="orange" testId="kpi-ca-total" />
                        <KpiCard icon={TrendingUp} label="CA ce mois" value={kpis.ca_this_month ?? 0} suffix="€" testId="kpi-ca-month" />
                        <KpiCard icon={Users} label="Inscriptions ce mois" value={kpis.enrollments_this_month ?? 0} testId="kpi-enrollments-month" />
                        <KpiCard icon={Percent} label="Taux de remplissage moyen" value={Math.round((kpis.avg_fill_rate || 0) * 100)} suffix="%" testId="kpi-fill-rate" />
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                        <KpiCard icon={CalendarDays} label="Sessions à venir" value={kpis.upcoming_sessions ?? 0} testId="kpi-upcoming" />
                        <KpiCard icon={UserCheck} label="Formateurs actifs" value={kpis.formateurs_active ?? 0} testId="kpi-formateurs" />
                        <KpiCard icon={Users} label="Stagiaires" value={kpis.stagiaires ?? 0} testId="kpi-stagiaires" />
                    </div>

                    <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
                        {/* CA mensuel */}
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium lg:col-span-2" data-testid="admin-chart-ca">
                            <h3 className="font-display text-2xl">Chiffre d'affaires par mois</h3>
                            <p className="mt-1 text-xs text-brand-smoke">12 derniers mois</p>
                            <div className="mt-6 h-64">
                                {(stats?.ca_monthly || []).length === 0 ? (
                                    <div className="flex h-full items-center justify-center text-sm text-brand-smoke">Aucune donnée pour l'instant.</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats.ca_monthly}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E7E3DC" />
                                            <XAxis dataKey="month" stroke="#8A8A8A" style={{ fontSize: 11 }} />
                                            <YAxis stroke="#8A8A8A" style={{ fontSize: 11 }} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E3DC" }} formatter={(v) => `${v} €`} />
                                            <Bar dataKey="amount" fill="#E8873C" radius={[8, 8, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>

                        {/* Donut cursus */}
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="admin-chart-cursus">
                            <h3 className="font-display text-2xl">Inscriptions par cursus</h3>
                            <div className="mt-6 h-64">
                                {(stats?.enrollments_by_cursus || []).length === 0 ? (
                                    <div className="flex h-full items-center justify-center text-sm text-brand-smoke">Aucune inscription.</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={stats.enrollments_by_cursus} dataKey="count" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={4}>
                                                {stats.enrollments_by_cursus.map((entry, i) => (
                                                    <Cell key={entry.slug} fill={PALETTE[i % PALETTE.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E3DC" }} />
                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Critical sessions */}
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="admin-critical-sessions">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                <h3 className="font-display text-2xl">Sessions critiques</h3>
                            </div>
                            <p className="mt-1 text-xs text-brand-smoke">Démarrent dans &lt; 30 jours · &lt; 50% rempli</p>
                            <div className="mt-6 divide-y divide-brand-line">
                                {(stats?.critical_sessions || []).length === 0 ? (
                                    <div className="py-6 text-sm text-brand-smoke">Aucune session critique 🎉</div>
                                ) : (
                                    stats.critical_sessions.map((s) => (
                                        <Link key={s.id} to="/admin/sessions" className="flex items-center justify-between py-3 hover:bg-[#FAFAFA]">
                                            <div>
                                                <div className="text-sm font-semibold text-brand-ink">{s.formation_name}</div>
                                                <div className="text-xs text-brand-smoke">{formatDateFR(s.start_date)} · {s.city}</div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="text-right text-xs">
                                                    <div className="font-semibold text-brand-ink">{s.booked}/{s.total_seats} places</div>
                                                    <div className="text-brand-smoke">J-{s.days_until}</div>
                                                </div>
                                                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-brand-line">
                                                    <div className="h-full bg-red-500" style={{ width: `${Math.round(s.fill_rate * 100)}%` }} />
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Last payments */}
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="admin-last-payments">
                            <div className="flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-brand-orange" />
                                <h3 className="font-display text-2xl">Derniers paiements</h3>
                            </div>
                            <div className="mt-6 divide-y divide-brand-line">
                                {(stats?.last_payments || []).length === 0 ? (
                                    <div className="py-6 text-sm text-brand-smoke">Aucun paiement pour l'instant.</div>
                                ) : (
                                    stats.last_payments.map((p) => (
                                        <div key={p.id} className="flex items-center justify-between py-3">
                                            <div>
                                                <div className="text-sm font-medium text-brand-ink">{p.user_email}</div>
                                                <div className="text-xs text-brand-smoke">{formatDateFR(p.updated_at)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-display text-lg text-brand-orange">{Math.round(p.amount_eur)} €</div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <Link to="/admin/inscriptions" className="inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline">
                            Voir toutes les inscriptions <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
}
